import { useMemo } from 'react'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts'

export type StageKey = 'purchases' | 'selection' | 'roasting' | 'blending' | 'packaging' | 'espera'

export interface TimelineRow {
  id: string // `${stageKey}-${raw.id}`
  stageKey: StageKey
  code: string
  status: string
  isActive: boolean
  isPointEvent: boolean // true only for stageKey === 'selection'
  isOngoing: boolean // no real end date yet (completed_date/received_date null, status not terminal)
  startDate: Date
  endDate: Date // == startDate if isPointEvent; == today if isOngoing
  label: string // Y-axis category text, e.g. `${stageLabel} · ${code}`
}

interface ChartRow extends TimelineRow {
  offsetDays: number
  durationDays: number
}

// Same hex values as AdminDashboardPage.tsx's COLORS array (indices 0, 2, 4, 1, 3),
// kept fixed per-stage rather than derived from array position: this is the legend,
// it must stay stable when a stage is toggled off.
const STAGE_COLORS: Record<StageKey, string> = {
  purchases: '#f59e0b',
  selection: '#3b82f6',
  roasting: '#ef4444',
  blending: '#10b981',
  packaging: '#8b5cf6',
  espera: '#9ca3af',
}

export const STAGE_LABELS: Record<StageKey, string> = {
  purchases: 'Compra',
  selection: 'Selección',
  roasting: 'Tostado',
  blending: 'Mezclado',
  packaging: 'Empaquetado',
  espera: 'Espera',
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pendiente',
  in_transit: 'En Tránsito',
  at_rest: 'En Almacén',
  consumed: 'Consumido',
  esperando: 'Esperando para avanzar de etapa',
}

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

function addDays(d: Date, n: number) {
  return new Date(d.getTime() + n * 86400000)
}

function diffDays(a: Date, b: Date) {
  return Math.round((startOfDay(a).getTime() - startOfDay(b).getTime()) / 86400000)
}

function CustomTooltip({ active, payload }: any) {
  if (!active || !payload || !payload.length) return null
  const row: ChartRow = payload[0].payload
  const fmt = (d: Date) => d.toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' })
  return (
    <div className="bg-cream-50 border border-espresso-200 rounded-md shadow-md px-3 py-2 text-xs max-w-xs">
      <p className="font-semibold text-espresso-800 mb-1">
        {STAGE_LABELS[row.stageKey]} · {row.code}
      </p>
      <p className="text-espresso-600">
        Estado: {STATUS_LABELS[row.status] || row.status}
        {row.isActive === false ? ' (Anulado)' : ''}
      </p>
      {row.isPointEvent ? (
        <p className="text-espresso-500 mt-1">
          Evento puntual — sin fecha de fin (esta etapa no tiene columna de finalización). Fecha: {fmt(row.startDate)}
        </p>
      ) : row.isOngoing ? (
        <p className="text-espresso-500 mt-1">En curso (sin fecha de fin aún). Inicio: {fmt(row.startDate)}</p>
      ) : (
        <p className="text-espresso-500 mt-1">
          {fmt(row.startDate)} → {fmt(row.endDate)}
        </p>
      )}
    </div>
  )
}

function renderStageBarOrDiamond(props: any) {
  const row: ChartRow = props.payload
  const fill = STAGE_COLORS[row.stageKey]
  if (row.isPointEvent) {
    const cx = props.x
    const cy = props.y + props.height / 2
    const r = 6
    const points = `${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`
    return <polygon points={points} fill={fill} stroke="#fff" strokeWidth={1} />
  }
  return (
    <rect
      x={props.x}
      y={props.y}
      width={props.width}
      height={props.height}
      rx={3}
      fill={fill}
      opacity={row.isOngoing ? 0.55 : 1}
    />
  )
}

// Custom legend content, rendered via Recharts' `content` prop (real recharts v3
// Legend no longer accepts a `payload` prop directly -- see DefaultLegendContent's
// own doc comment). Built as plain interactive buttons instead of relying on
// Legend's internal onClick wiring, so it gets real keyboard focus + aria state.
function GanttLegend({
  hiddenStages,
  onToggleStage,
}: {
  hiddenStages: Set<StageKey>
  onToggleStage: (key: StageKey) => void
}) {
  const stageKeys = Object.keys(STAGE_LABELS) as StageKey[]
  return (
    <ul className="flex flex-wrap gap-3 justify-center mt-2">
      {stageKeys.map((key) => {
        const isHidden = hiddenStages.has(key)
        return (
          <li key={key}>
            <button
              type="button"
              onClick={() => onToggleStage(key)}
              aria-pressed={!isHidden}
              title={isHidden ? `Mostrar ${STAGE_LABELS[key]}` : `Ocultar ${STAGE_LABELS[key]}`}
              className={`flex items-center gap-1.5 text-xs px-1.5 py-0.5 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500 ${
                isHidden ? 'text-espresso-400' : 'text-espresso-700'
              }`}
            >
              <span
                className="inline-block w-3 h-3 rounded-sm"
                style={{ backgroundColor: isHidden ? '#d1d5db' : STAGE_COLORS[key] }}
              />
              {STAGE_LABELS[key]}
            </button>
          </li>
        )
      })}
    </ul>
  )
}

/**
 * Presentational Gantt-style timeline for the 5-stage production chain.
 *
 * `hiddenStages`/`onToggleStage` extend the `{ rows }`-only prop contract
 * originally sketched for this component: the per-stage visibility toggle is
 * meant to live in this chart's own Legend (not a separate widget), and the
 * Legend can only render here, so TimelineTab needs a way to (a) tell this
 * component which stages are currently toggled off so the legend renders the
 * right state, and (b) hear about clicks. `rows` is still expected to already
 * be filtered by TimelineTab (date range + visibility) before it gets here.
 */
export default function GanttChart({
  rows,
  hiddenStages,
  onToggleStage,
}: {
  rows: TimelineRow[]
  hiddenStages: Set<StageKey>
  onToggleStage: (key: StageKey) => void
}) {
  const { chartData, totalDays, ticks, chartStart } = useMemo(() => {
    if (rows.length === 0) {
      return { chartData: [] as ChartRow[], totalDays: 1, ticks: [0], chartStart: startOfDay(new Date()) }
    }

    const today = new Date()
    let chartStart = rows.reduce((min, r) => (r.startDate < min ? r.startDate : min), rows[0].startDate)
    chartStart = startOfDay(chartStart)
    let chartEnd = rows.reduce((max, r) => (r.endDate > max ? r.endDate : max), rows[0].endDate)
    if (today > chartEnd) chartEnd = today
    chartEnd = startOfDay(chartEnd)

    const totalDays = Math.max(diffDays(chartEnd, chartStart), 1)

    const chartData: ChartRow[] = rows.map((row) => {
      const offsetDays = diffDays(row.startDate, chartStart)
      const durationDays = row.isPointEvent ? 0.4 : Math.max(diffDays(row.endDate, row.startDate), 0.6)
      return { ...row, offsetDays, durationDays }
    })

    let step = 1
    if (totalDays > 45) step = 7
    else if (totalDays > 14) step = 3
    const ticks: number[] = []
    for (let t = 0; t <= totalDays; t += step) ticks.push(t)
    if (ticks[ticks.length - 1] !== totalDays) ticks.push(totalDays)

    return { chartData, totalDays, ticks, chartStart }
  }, [rows])

  const naturalHeight = Math.max(360, rows.length * 26)
  const containerHeight = Math.min(700, naturalHeight)
  const isCapped = naturalHeight > 700

  if (rows.length === 0) {
    return <p className="text-espresso-500 text-center py-8">No hay registros en este rango de fechas.</p>
  }

  return (
    <div style={{ height: containerHeight }} className={isCapped ? 'overflow-y-auto' : undefined}>
      <ResponsiveContainer width="100%" height={naturalHeight}>
        <BarChart data={chartData} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            type="number"
            domain={[0, totalDays]}
            ticks={ticks}
            tickFormatter={(value: number) =>
              addDays(chartStart, value).toLocaleDateString('es', { day: '2-digit', month: 'short' })
            }
          />
          <YAxis type="category" dataKey="label" width={160} tick={{ fontSize: 11 }} />
          <Tooltip content={CustomTooltip} />
          <Legend content={() => <GanttLegend hiddenStages={hiddenStages} onToggleStage={onToggleStage} />} />
          <Bar dataKey="offsetDays" stackId="gantt" fill="transparent" isAnimationActive={false} legendType="none" />
          <Bar dataKey="durationDays" stackId="gantt" name="Etapa" shape={renderStageBarOrDiamond} legendType="none" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
